import { useState } from 'react';
import { Button, Chip, Stack, Tooltip } from '@mui/material';

export interface WorkClaimState {
  claimed_by_id?: string | null;
  claimed_by_name?: string | null;
  claimed_at?: string | null;
  is_claimed_by_me?: boolean;
}

export type WorkClaimAction = 'claim' | 'release' | 'reset';

interface Props {
  claim: WorkClaimState;
  onClaim: () => Promise<unknown>;
  onRelease: () => Promise<unknown>;
  onAdminReset?: () => Promise<unknown>;
  canAdminReset?: boolean;
  allowClaim?: boolean;
  onChanged: (result: unknown, action: WorkClaimAction) => void | Promise<void>;
  onError?: (error: unknown) => void;
}

export default function WorkClaimButton({
  claim,
  onClaim,
  onRelease,
  onAdminReset,
  canAdminReset = false,
  allowClaim = true,
  onChanged,
  onError,
}: Props) {
  const [busy, setBusy] = useState(false);

  const run = async (
    action: () => Promise<unknown>,
    actionType: WorkClaimAction,
  ) => {
    setBusy(true);
    try {
      const result = await action();
      await onChanged(result, actionType);
    } catch (error) {
      onError?.(error);
    } finally {
      setBusy(false);
    }
  };

  const adminResetButton = canAdminReset && onAdminReset ? (
    <Button
      size="small"
      color="warning"
      disabled={busy}
      onClick={() => {
        if (window.confirm('Освободить задачу и очистить блокирующие записи обработки?')) {
          void run(onAdminReset, 'reset');
        }
      }}
    >
      Сбросить обработку
    </Button>
  ) : null;

  if (claim.is_claimed_by_me) {
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Chip size="small" color="success" label="В работе у меня" />
        <Button size="small" disabled={busy} onClick={() => void run(onRelease, 'release')}>
          Освободить
        </Button>
        {adminResetButton}
      </Stack>
    );
  }

  if (claim.claimed_by_id) {
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Tooltip title="Действия доступны только сотруднику, который взял задачу">
          <Chip
            size="small"
            color="warning"
            label={`В работе: ${claim.claimed_by_name || claim.claimed_by_id}`}
          />
        </Tooltip>
        {adminResetButton}
      </Stack>
    );
  }

  if (!allowClaim) return adminResetButton;

  return (
    <Stack spacing={0.5} alignItems="flex-start">
      <Button size="small" variant="outlined" disabled={busy} onClick={() => void run(onClaim, 'claim')}>
        Взять на себя
      </Button>
      {adminResetButton}
    </Stack>
  );
}
